import { createSignal, Show } from 'solid-js';
import { state, hideModal, shareAsRoom, joinRoomByUrl, renameItem, pushToast, leaveRoom, saveRoomToFileAndLeave } from '../state';

export function Modals() {
  return (
    <>
      <Show when={state.modal === 'share'}><ShareModal /></Show>
      <Show when={state.modal === 'join'}><JoinModal /></Show>
      <Show when={state.modal === 'rename'}><RenameModal /></Show>
      <Show when={state.modal === 'leave-room'}><LeaveRoomModal /></Show>
    </>
  );
}

function LeaveRoomModal() {
  const roomId = state.modalArg!;
  const room = () => state.rooms.find((r) => r.id === roomId);
  return (
    <div class="modal-backdrop" onClick={hideModal}>
      <div class="modal leave-md-modal" onClick={(e) => e.stopPropagation()}>
        <div class="md-mock">
          <span class="md-fence">---</span>
          <span class="md-fm-row"><span class="md-fm-key">title:</span> <span class="md-fm-val">{room()?.title ?? 'unsaved room'}</span></span>
          <span class="md-fm-row"><span class="md-fm-key">status:</span> <span class="md-fm-val">alone</span></span>
          <span class="md-fm-row"><span class="md-fm-key">peers:</span> <span class="md-fm-val">0</span></span>
          <span class="md-fm-row"><span class="md-fm-key">risk:</span> <span class="md-fm-val">edits stay in this browser only</span></span>
          <span class="md-fence">---</span>
          <span class="md-blank"></span>
          <span class="md-h"># You're the last one here</span>
          <span class="md-blank"></span>
          <span class="md-p">Your edits live in this browser. If you leave now, anyone who opens the share link later <span class="md-strong">**won't see your latest changes**</span> until you rejoin and reconnect.</span>
          <span class="md-blank"></span>
          <span class="md-q">&gt; Save to <span class="md-strong">**My files**</span> for a portable copy that survives anything.</span>
          <span class="md-blank"></span>
          <span class="md-comment">&lt;!-- diff: unsaved-in-this-browser --&gt;</span>
          <span class="md-diff-add">+ your latest edits</span>
          <span class="md-diff-add">+ visible only to you</span>
          <span class="md-diff-del">- visible to future peers</span>
        </div>
        <div class="md-actions">
          <button class="md-link" onClick={hideModal}>[ Stay ]<span class="md-link-href">(#stay)</span></button>
          <button class="md-link primary" onClick={async () => {
            await saveRoomToFileAndLeave(roomId);
            hideModal();
          }}>[ Save &amp; leave ]<span class="md-link-href">(#commit)</span></button>
          <button class="md-link danger" onClick={async () => {
            await leaveRoom(roomId);
            hideModal();
            pushToast({ severity: 'warn', title: 'Left the room', body: 'Your latest edits stay in this browser only.' });
          }}>[ Leave anyway ]<span class="md-link-href">(#yolo)</span></button>
        </div>
      </div>
    </div>
  );
}

function ShareModal() {
  const fileId = state.modalArg!;
  const file = () => state.files.find((f) => f.id === fileId);
  return (
    <div class="modal-backdrop" onClick={hideModal}>
      <div class="modal" onClick={(e) => e.stopPropagation()}>
        <div class="modal-head">
          <h2>Share <em>{file()?.title ?? 'this file'}</em>?</h2>
          <div class="sub">This will turn it into a collaborative room.</div>
        </div>
        <div class="modal-body">
          <p>The file will move from <strong>My files</strong> to <strong>Rooms</strong>. Anyone with the link can edit. Updates sync peer-to-peer.</p>
          <div class="callout">Live collaboration is coming once Yjs/WebRTC is wired in. For now, sharing creates the room locally.</div>
        </div>
        <div class="modal-actions">
          <button class="btn" onClick={hideModal}>Cancel</button>
          <button class="btn primary" onClick={async () => {
            const roomId = await shareAsRoom(fileId);
            hideModal();
            if (roomId) {
              pushToast({
                severity: 'success',
                title: 'Shared as room',
                body: 'Anyone with the link can edit.',
                actions: [{
                  label: 'Copy link',
                  run: () => {
                    navigator.clipboard?.writeText(`${location.origin}/#room=${roomId}`).catch(() => {});
                    pushToast({ severity: 'success', title: 'Link copied' });
                  },
                }],
              });
            }
          }}>Share</button>
        </div>
      </div>
    </div>
  );
}

function JoinModal() {
  const [url, setUrl] = createSignal('');
  const valid = () => /#room=[\w-]+/.test(url().trim());
  let inputRef: HTMLInputElement | undefined;
  queueMicrotask(() => inputRef?.focus());
  return (
    <div class="modal-backdrop" onClick={hideModal}>
      <div class="modal" onClick={(e) => e.stopPropagation()}>
        <div class="modal-head">
          <h2>Join a room</h2>
          <div class="sub">Paste a share link to join.</div>
        </div>
        <div class="modal-body">
          <input
            ref={inputRef}
            class="field"
            placeholder={`${location.origin}/#room=…`}
            value={url()}
            onInput={(e) => setUrl(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && valid()) (e.currentTarget.closest('.modal')!.querySelector('.btn.primary') as HTMLElement)?.click(); }}
          />
          <div class="callout">Joining adds the room to your sidebar.</div>
        </div>
        <div class="modal-actions">
          <button class="btn" onClick={hideModal}>Cancel</button>
          <button class="btn primary" disabled={!valid()} onClick={async () => {
            const ok = await joinRoomByUrl(url());
            hideModal();
            if (ok) pushToast({ severity: 'success', title: 'Joined room' });
            else pushToast({ severity: 'danger', title: 'Invalid link' });
          }}>Join</button>
        </div>
      </div>
    </div>
  );
}

function RenameModal() {
  const id = state.modalArg!;
  const initial = () => {
    const f = state.files.find((x) => x.id === id);
    if (f) return f.title;
    const r = state.rooms.find((x) => x.id === id);
    return r?.title ?? '';
  };
  const [val, setVal] = createSignal(initial());
  let inputRef: HTMLInputElement | undefined;
  queueMicrotask(() => { inputRef?.focus(); inputRef?.select(); });
  const submit = async () => {
    const v = val().trim();
    if (!v) return;
    await renameItem(id, v);
    hideModal();
    pushToast({ severity: 'success', title: 'Renamed' });
  };
  return (
    <div class="modal-backdrop" onClick={hideModal}>
      <div class="modal" onClick={(e) => e.stopPropagation()}>
        <div class="modal-head">
          <h2>Rename</h2>
        </div>
        <div class="modal-body">
          <input
            ref={inputRef}
            class="field"
            value={val()}
            onInput={(e) => setVal(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
          />
        </div>
        <div class="modal-actions">
          <button class="btn" onClick={hideModal}>Cancel</button>
          <button class="btn primary" onClick={submit}>Rename</button>
        </div>
      </div>
    </div>
  );
}
